import { ContainerComponent, wrap, today } from 'AppReact';
import Controller from './controller';
import internalState from './internalState';
import {
  FlexCenterWrapper, Th, Mr4, Td, WorkOrderRowWrapper,
  UnderlineDecoration, TooltipWrapper, Mr12, Mr1,
  CustomWidthHeight
} from 'wrappers';
import {
  CategoryCell, CancellationCell, RecursTimeCell,
  ParentCategoryCell
} from './molecules';
import { Rating, CalendarDateCell } from 'molecules';
import { SecondaryTextSm, ClickableIcon, Empty, Tooltip, ProfileImage, Initials } from 'atoms';
import { differenceInDays, format, isToday, isValid } from 'date-fns';
import { CheckboxSvg, LocationSvg, HomeSvg, UserSvg } from 'components/icons';
import { getInitials } from 'utils/getInitials';

export const definition = {
  name: 'WorkOrderTableRow',
  controller: Controller,
  initialProps: ({ order, isSelected, selectedTab }) => ({ order, isSelected, selectedTab }),
  props: internalState,
  elements: [
    {
      wrapper: WorkOrderRowWrapper,
      name: 'Wrapper',
      isButton: true,
      bindings: ({ }) => ({}),
      elements: [
        {
          wrapper: wrap(wrap(FlexCenterWrapper, Mr12), Th),
          name: 'Category',
          elements: [
            {
              component: wrap(ClickableIcon, Mr4),
              name: 'Checkbox',
              displayValue: ({ isSelected }) => CheckboxSvg({ checked: isSelected }),
              bindings: ({ }) => ({})
            },
            {
              wrapper: CustomWidthHeight,
              displayState: ({ }) => ({ width: '400px' }),
              name: '__transparent__',
              elements: [
                {
                  component: CategoryCell,
                  name: 'Name',
                  bindings: ({ order }) => ({ order })
                }
              ]
            }
          ]
        },
        {
          component: wrap(wrap(ParentCategoryCell, Mr12), Td),
          name: 'ParentCategory',
          bindings: ({ order }) => ({ order })
        },
        {
          wrapper: wrap(wrap(FlexCenterWrapper, Mr12), Td),
          name: 'LocationCell',
          elements: [
            {
              component: wrap(Empty, Mr1),
              name: 'Icon',
              displayValue: ({ }) => LocationSvg({ width: '12', height: '14', color: window?.settings?.secondary_color ?? '#04333b' }),
            },
            {
              component: wrap(SecondaryTextSm, Mr1),
              name: 'Property',
              displayValue: ({ order }) => order?.property_name ?? '',
            },
            {
              wrapper: TooltipWrapper,
              name: 'Unit',
              isButton: true,
              renderIf: ({ order }) => order?.unit_number,
              bindings: ({ order }) => ({ order }),
              elements: [
                {
                  component: wrap(SecondaryTextSm, UnderlineDecoration),
                  name: 'Unit',
                  displayValue: ({ order }) => order?.unit_number ?? '',
                },
                {
                  component: Tooltip,
                  name: 'Popup',
                  displayValue: ({ }) => 'Click to see unit',
                  displayState: ({ }) => ({ width: '130px' })
                }
              ]
            },
          ]
        },
        {
          wrapper: wrap(wrap(FlexCenterWrapper, Mr12), Td),
          name: 'User',
          elements: [
            {
              component: wrap(Empty, Mr1),
              name: 'Icon',
              renderIf: ({ order, selectedTab }) => order.status === 'unassigned' && selectedTab !== 'recurring',
              displayValue: ({ order }) => !order.tenancy_id ? HomeSvg({ width: '12', height: '14', color: window?.settings?.secondary_color ?? '#04333b' }) : UserSvg({ width: '12', height: '14', color: window?.settings?.secondary_color ?? '#04333b' }),
            },
            {
              component: wrap(ProfileImage, Mr1),
              name: 'Image',
              renderIf: ({ order }) => ['assigned', 'completed'].includes(order.status) && (order.tech_image || order.vendor_url),
              displayValue: ({ order }) => order.tech_image ?? order.vendor_url ?? '',
              displayState: ({ }) => ({ size: '14' })
            },
            {
              component: wrap(Initials, Mr1),
              name: 'Initials',
              renderIf: ({ order }) => order.status === 'cancelled' || (['assigned', 'completed'].includes(order.status) && !(order.tech_image || order.vendor_url)),
              displayValue: ({ order }) => {
                let userName = '';

                if (['assigned', 'completed'].includes(order.status)) {
                  userName = order.tech_name ?? order.vendor_name;
                }

                if (order.status === 'cancelled') {
                  userName = order.cancelled_by;
                }

                return getInitials(userName) ?? '';
              },
              displayState: ({ }) => ({ size: '14' })
            },
            {
              wrapper: TooltipWrapper,
              name: 'Unit',
              isButton: true,
              bindings: ({ order }) => ({ order }),
              elements: [
                {
                  component: SecondaryTextSm,
                  name: 'Unit',
                  displayValue: ({ order, selectedTab }) => {
                    let userName = '';

                    if (order.status === 'unassigned') {
                      if (!order.tenancy_id && order.unit_id) {
                        userName = selectedTab !== 'recurring' ? 'Vacant Unit' : 'Unassigned';
                      } else if (!order.tenancy_id) {
                        userName = 'Property Work Order';
                      } else {
                        userName = order.tenant_name ?? '';
                      }
                    }

                    if (order.status === 'assigned') {
                      userName = order.tech_name ?? order.vendor_name ?? '';
                    }

                    if (order.status === 'completed') {
                      userName = order.tech_name ?? order.vendor_name ?? '';
                    }

                    if (order.status === 'cancelled') {
                      userName = order.cancelled_by ?? ''
                    }

                    return userName ?? ''
                  },
                },
                {
                  component: Tooltip,
                  renderIf: ({ order }) => (order.status === 'unassigned' && order.tenancy_id) || (order.status === 'assigned') || order.status === 'completed',
                  name: 'Popup',
                  displayValue: ({ order }) => {
                    let userName = '';

                    if (order.status === 'unassigned') {
                      userName = 'Click to see resident';
                    }

                    if (order.status === 'assigned') {
                      userName = order.tech_name ? 'Click to see technician' : 'Click to see vendor';
                    }

                    if (order.status === 'completed') {
                      userName = 'Completed order';
                    }

                    return userName;
                  },
                }
              ]
            },
          ]
        },
        {
          component: wrap(wrap(CalendarDateCell, Mr12), Td),
          name: 'AssignationTime',
          renderIf: ({ selectedTab }) => ['assigned', 'unassigned'].includes(selectedTab),
          bindings: ({ order }) => ({ date: order.scheduled_for })
        },
        {
          component: wrap(wrap(Rating, Mr12), Td),
          name: 'Rating',
          renderIf: ({ selectedTab }) => selectedTab === 'completed',
          bindings: ({ order }) => ({ rating: order.rating ?? 0 })
        },
        {
          component: wrap(wrap(CancellationCell, Mr12), Td),
          name: 'CancellationReason',
          renderIf: ({ selectedTab }) => selectedTab === 'cancelled',
          bindings: ({ order }) => ({ order })
        },
        {
          component: wrap(RecursTimeCell, Td),
          name: 'Reccurs',
          renderIf: ({ selectedTab }) => selectedTab === 'recurring',
          bindings: ({ order }) => ({ order })
        },

        {
          component: wrap(SecondaryTextSm, Td),
          name: 'TimeCell',
          renderIf: ({ selectedTab }) => ['unassigned', 'assigned', 'completed', 'cancelled'].includes(selectedTab),
          displayValue: ({ order }) => {
            let time = new Date(order.inserted_at);
            let displayValue = '';

            if (order.status === 'assigned') {
              time = new Date(order.assigned_at);
            }

            if (order.status === 'completed') {
              time = new Date(order.completed_at);
            }

            if (order.status === 'cancelled') {
              time = new Date(order.cancelled_at)
            }


            if (isToday(time)) {
              displayValue = format(time, 'hh:mm a');
              return displayValue;
            }

            if (differenceInDays(today(), time) < 7) {
              displayValue = format(time, 'EEEE');
              return displayValue;
            }


            if (!isValid(time)) return '';

            displayValue = time ? format(time, 'MM/dd/yy') : '';

            return displayValue;
          }
        },
      ]
    }
  ]
};

export default ContainerComponent(definition)